import { DatePipe } from '@angular/common';
import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { UIChart } from 'primeng/chart';
import { lastValueFrom, map } from 'rxjs';
import {
  KpiControllerService,
  SubscriptionsPerHour,
  SubscriptionsPerHourRealtime,
  SubscriptionsTotalResponse
} from 'src/app/admin-api';
import {
  getAllSubscriptionsIncludeAll,
  getSubscriptionName,
  Message,
  SubscriberUpdate,
  Topic
} from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { WebsocketService } from 'src/app/services/websocket.service';

@Component({
  selector: 'app-analytics-dashboard',
  templateUrl: './analytics-dashboard.component.html',
  styleUrl: './analytics-dashboard.component.scss'
})
export class AnalyticsDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('chart') chart: UIChart;

  @Input()
  defaultSubscriptionId: number;

  @Input()
  show: WritableSignal<boolean>;

  @Input()
  subscribersRealtimeTotal: WritableSignal<SubscriptionsTotalResponse> =
    signal(undefined);

  subscriptionId = new FormControl<number>(0, [
    Validators.required,
    Validators.min(0)
  ]);
  subscriptions: Array<{ label: string; value: number }>;
  width: string;
  chartData: ChartData<'line'> = {
    labels: this.yAxis,
    datasets: [
      {
        data: this.yAxis.map(() => 0),
        label: 'Média Vendas/Hora | (Últimos 7 dias)',
        type: 'line',
        backgroundColor: '#00aaff',
        borderColor: '#00aaff',
        tension: 0.4
      },
      {
        data: this.yAxis.map(() => 0),
        label: 'Vendas/Hora | Dia Atual',
        type: 'line',
        
        backgroundColor: '#ffffff',
        borderColor: '#ffffff',
        pointBackgroundColor: '#606a74',
        pointBorderColor: '#606a74',
        fill: false,
        tension: 0.4
      }
    ]
  };
  chartConfig: ChartOptions<'line'> = {
    animation: {
      easing: 'easeInQuad'
    },
    plugins: {
      legend: {
        labels: {
          color: '#ffffff',
          font: {
            family: 'Inter, Montserrat, "Segoe UI", sans-serif',
            size: 13,
            weight: 600
          }
        }
      },
      tooltip: {
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        footerColor: '#ffffff'
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#ffffff',
          font: {
            family: 'Inter, Montserrat, "Segoe UI", sans-serif',
            weight: 500
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.08)'
        }
      },
      y: {
        ticks: {
          color: '#ffffff',
          font: {
            family: 'Inter, Montserrat, "Segoe UI", sans-serif',
            weight: 500
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.08)'
        }
      }
    },
    elements: {
      point: {
        radius: 5
      }
    }
  };
  subscribers: Array<SubscriptionsPerHour> | undefined;
  subscribersRealtime: Array<SubscriptionsPerHourRealtime> | undefined;
  interval: NodeJS.Timeout;
  lastUpdate = new Date();

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkScreenSize();
    this.updateChartHeight();
  }
  chartHeight = '58vh';

  constructor(
    private kpiService: KpiControllerService,
    private title: Title,
    private datePipe: DatePipe,
    private websocketService: WebsocketService
  ) {
    this.subscriptions = getAllSubscriptionsIncludeAll();
    this.checkScreenSize();
    this.updateChartHeight();
    if (this.defaultSubscriptionId === undefined)
      this.title.setTitle('Dashboard de frequências');
  }

  checkScreenSize() {
    delete this.width;
    setTimeout(() => {
      this.width = window.innerWidth > 768 ? '100%' : '500px';
      this.updateChartHeight();
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.defaultSubscriptionId !== undefined) {
      this.subscriptionId.setValue(this.defaultSubscriptionId);
    }
    if (!this.subscribersRealtimeTotal) {
      this.connect();
      this.subscribersRealtimeTotal = signal(undefined);
    }
    await this.findPage(true);
    this.startIntervals();
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
      delete this.interval;
    }
    this.websocketService.disconnect();
  }

  startIntervals() {
    let lastUpdate = new Date();
    this.interval = setInterval(
      async () => {
        const now = new Date();
        await this.findPage(now.getDate() !== lastUpdate.getDate());
        lastUpdate = now;
      },
      1000 * 60 * 30
    );
  }

  async changeSubscription(): Promise<void> {
    delete this.subscribers;
    await this.findPage(true);
  }

  async findPage(findSubscriptions = false): Promise<void> {
    await Promise.all(
      [this.findSubscriptionsRealtime()].concat(
        findSubscriptions ? [this.findSubscriptions()] : []
      )
    );
    this.lastUpdate = new Date();
    this.startChart();
  }

  startChart() {
    this.chartData.datasets[0].data = this.yAxis.map(
      (date) =>
        this.subscribers
          .filter(
            (s) =>
              Number(this.datePipe.transform(s.referenceDate, 'HH')) + 'h' ===
              date
          )
          .reduce((sum, s) => (sum += s.subscriptions), 0) / 7
    );
    this.chartData.datasets[1].data = this.yAxis
      .filter(
        (hour) =>
          Number(this.datePipe.transform(new Date(), 'HH')) >=
          Number(hour.replace('h', ''))
      )
      .map((date) =>
        this.subscribersRealtime
          .filter(
            (s) =>
              Number(this.datePipe.transform(s.referenceDate, 'HH')) + 'h' ===
              date
          )
          .reduce((sum, s) => (sum += s.subscriptions), 0)
      );
    this.fixChartColors();
    this.chart?.chart?.update();
  }

  private updateChartHeight(): void {
    const fullscreenThreshold = 40;
    const isFullscreen =
      Math.abs(window.innerHeight - window.screen.height) < fullscreenThreshold &&
      Math.abs(window.innerWidth - window.screen.width) < fullscreenThreshold;
    this.chartHeight = isFullscreen ? '54vh' : '56vh';
  }

  async findSubscriptions(): Promise<void> {
    try {
      this.subscribers = await lastValueFrom(
        this.kpiService.findAnalyticsDashboard(this.subscriptionId.value).pipe(
          map((data) =>
            data.result.map((s) => ({
              ...s,
              referenceDate: new Date(s.referenceDate)
            }))
          )
        )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findSubscriptionsRealtime(): Promise<void> {
    try {
      this.subscribersRealtime = await lastValueFrom(
        this.kpiService
          .findAnalyticsDashboardRealtime(this.subscriptionId.value)
          .pipe(
            map((data) =>
              data.result.map((s) => ({
                ...s,
                referenceDate: new Date(s.referenceDate)
              }))
            )
          )
      );
      if (!this.subscribersRealtimeTotal()?.createdAt)
        this.findSubscriptionsRealtimeCount();
    } catch (error) {
      console.error(error);
    }
  }

  async findSubscriptionsRealtimeCount(): Promise<void> {
    try {
      const total = await lastValueFrom(
        this.kpiService
          .findAnalyticsDashboardRealtimeTotal(this.subscriptionId.value)
          .pipe(
            map((data) => ({
              ...data.result,
              createdAt: new Date(data.result.createdAt)
            }))
          )
      );
      if (
        !this.subscribersRealtimeTotal()?.total ||
        !this.subscribersRealtimeTotal()?.createdAt ||
        total.total > this.subscribersRealtimeTotal().total ||
        total.createdAt.getTime() >
          this.subscribersRealtimeTotal().createdAt.getTime()
      )
        this.subscribersRealtimeTotal.set(total);
    } catch (error) {
      console.error(error);
    }
  }

  hourForecast(hour: number): number {
    if (!hour)
      return (
        this.subscribers
          .filter(
            (s) =>
              Number(this.datePipe.transform(s.referenceDate, 'HH')) === hour
          )
          .reduce((sum, s) => (sum += s.subscriptions), 0) / 7
      );
    return (
      (this.subscribers
        .filter(
          (s) => Number(this.datePipe.transform(s.referenceDate, 'HH')) === hour
        )
        .reduce((sum, s) => (sum += s.subscriptions), 0) /
        7) *
      (this.subscribersRealtime
        .filter(
          (s) =>
            Number(this.datePipe.transform(s.referenceDate, 'HH')) <= hour - 1
        )
        .reduce((sum, s) => (sum += s.subscriptions), 0) /
        (this.subscribers
          .filter(
            (s) =>
              Number(this.datePipe.transform(s.referenceDate, 'HH')) <= hour - 1
          )
          .reduce((sum, s) => (sum += s.subscriptions), 0) /
          7) || 1)
    );
  }

  connect() {
    try {
      this.websocketService.connect(this.onMessage.bind(this));
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  onMessage(message: Message<any>): void {
    console.log(message);
    if (message.topic === Topic.SUBSCRIBER_UPDATE) {
      this.addNewSubscriber({
        ...message.body,
        dateAuthorized: new Date(message.body.dateAuthorized)
      });
    }
  }

  addNewSubscriber(subscriber: SubscriberUpdate) {
    let counter: SubscriptionsPerHourRealtime;
    if (![0, 2, 8].includes(subscriber.creditCardPaymentTypeId)) return;
    const acquisitionType =
      subscriber.creditCardPaymentTypeId === 0
        ? 1
        : subscriber.creditCardPaymentTypeId;
    counter = this.subscribersRealtime.find(
      (s) =>
        s.acquisitionType === acquisitionType &&
        s.subscriptionId === subscriber.subscriptionId &&
        this.datePipe.transform(s.referenceDate, 'yyyy-MM-dd HH:00:00') ===
          this.datePipe.transform(
            subscriber.dateAuthorized,
            'yyyy-MM-dd HH:00:00'
          )
    );
    if (counter) {
      counter.subscriptions++;
    } else {
      counter = {
        acquisitionType,
        referenceDate: new Date(
          this.datePipe.transform(
            subscriber.dateAuthorized,
            'yyyy-MM-dd HH:00:00'
          )
        ),
        subscriptionId: subscriber.subscriptionId,
        subscriptionName: getSubscriptionName(subscriber.subscriptionId),
        subscriptions: 1
      };
      this.subscribersRealtime.push(counter);
    }
    this.subscribersRealtimeTotal.set({
      createdAt: subscriber.dateAuthorized,
      total: this.subscribersRealtimeTotal().total + 1
    });
    const yIndex = this.yAxis.findIndex((l) => {
      const hour =
        Number(this.datePipe.transform(counter.referenceDate, 'HH')) + 'h';
      return l === hour;
    });
    if (yIndex >= 0) {
      this.chartData.datasets[1].data[yIndex] =
        ((this.chartData.datasets[1].data[yIndex] as number) || 0) + 1;
      this.fixChartColors();
      this.chart.chart.update();
    }
  }

  private fixChartColors() {
    const dataset = this.chartData.datasets[1] as ChartDataset<'line', number[]>;
    const colors = Array.from({ length: dataset.data.length }, () => '#606a74');

    for (let index = dataset.data.length - 1; index >= 0; index--) {
      const value = dataset.data[index];
      if (value !== null && value !== undefined) {
        colors[index] = '#ff7100';
        break;
      }
    }

    dataset.pointBackgroundColor = colors;
    dataset.pointBorderColor = colors;
    dataset.pointHoverBackgroundColor = colors;
    dataset.pointHoverBorderColor = colors;
    dataset.borderColor = '#ffffff';
    dataset.backgroundColor = '#ffffff';
  }

  get yAxis(): Array<string> {
    const hours: Array<string> = [];
    for (let index = 0; index < 24; index++) {
      hours.push(index + 'h');
    }
    return hours;
  }

  get totalSubscriptionsTillTime(): number {
    return (
      this.subscribers
        .filter(
          (s) =>
            Number(this.datePipe.transform(s.referenceDate, 'HH')) <
            Number(this.datePipe.transform(new Date(), 'HH'))
        )
        .reduce((sum, s) => (sum += s.subscriptions || 0), 0) / 7
    );
  }

  get totalSubscriptionsRealtimeTillTime(): number {
    return this.subscribersRealtime
      .filter(
        (s) =>
          Number(this.datePipe.transform(s.referenceDate, 'HH')) <
          Number(this.datePipe.transform(new Date(), 'HH'))
      )
      .reduce((sum, s) => (sum += s.subscriptions || 0), 0);
  }

  get avgSubscriptions(): number {
    return (
      this.subscribers.reduce((sum, s) => (sum += s.subscriptions || 0), 0) / 7
    );
  }

  get subscriptionsForecast(): number {
    return Math.round(
      this.avgSubscriptions *
        ((this.totalSubscriptionsRealtimeTillTime || 0) /
          (this.totalSubscriptionsTillTime || 1))
    );
  }

  get subscription(): { label: string; value: number } {
    return this.subscriptions.find(
      (s) => s.value === this.subscriptionId.value
    );
  }
}
